import { useEffect } from "react";

/**
 * Keeps Interstate's left/right tab scroll chevrons in sync with the strip's REAL overflow.
 *
 * `Tabs` hardcodes MUI's `variant="scrollable"` with `scrollButtons="auto"` and exposes no prop to
 * change it, but its own overflow check permanently misreports — so the chevrons show or hide
 * regardless of whether the strip actually overflows. This recomputes both conditions from the
 * scroller's own metrics and toggles each button's `display` directly.
 *
 * Originally inline in `MyInfo` (ported from `TabContainer.tsx`, PR #3897) and shared here so both
 * areas get it. `UserManager` needs it MORE, not less: its strip sits beside the user-list rail, so
 * the same four tabs overflow at ordinary desktop widths where My Info's full-width strip does not.
 *
 * Pass the same `id` given to `Tabs`.
 *
 * ## ⚠️ The strip does not have to exist yet, and may unmount and come back
 *
 * This hook is called at the top of both shells, **above their early returns** — it is a hook, so
 * it cannot sit beside the `Tabs` element behind those branches. In `UserManager` that means the
 * effect first runs while the page is still on its authority-pending gate and `#usermanager-tabs`
 * is nowhere in the DOM; the strip also unmounts entirely whenever no user is selected and mounts
 * **as a new node** when one is picked again.
 *
 * So the root is resolved by `id` on every mutation rather than captured once, and the observer is
 * attached to `document.body`. Keying the effect off a caller-supplied "is the strip rendered yet"
 * flag was the alternative and is worse: it restates each shell's render condition in a second
 * place, where it can drift from the JSX that actually decides.
 *
 * **This is one observer where the old code had one, not an extra.** It replaces the root-subtree
 * observer rather than adding to it, and the per-batch cost is the same layout read as before plus
 * a `getElementById` — the identity check below short-circuits everything else.
 */
export const useTabScrollButtons = (tabsId: string) => {
  useEffect(() => {
    let scroller: HTMLElement | undefined;

    const updateScrollButtonVisibility = () => {
      const root = document.getElementById(tabsId);
      const currentScroller =
        root?.querySelector<HTMLElement>(".MuiTabs-scroller") ?? undefined;

      // Re-point the scroll listener whenever the strip is a different node than last time — mount, unmount and remount all land here.
      if (currentScroller !== scroller) {
        scroller?.removeEventListener("scroll", updateScrollButtonVisibility);
        currentScroller?.addEventListener(
          "scroll",
          updateScrollButtonVisibility
        );
        scroller = currentScroller;
      }
      if (!root || !scroller) return;

      // Either button may be absent; match each by its DOM position relative to the scroller, not CSS offsets (which real browsers can resolve to a used pixel value on both sides).
      const arrowButtons = Array.from(
        root.querySelectorAll<HTMLElement>(
          ":scope > [data-ids-react-component-name='ids-react-noTag']"
        )
      );
      const startButton = arrowButtons.find(
        button =>
          scroller!.compareDocumentPosition(button) &
          Node.DOCUMENT_POSITION_PRECEDING
      );
      const endButton = arrowButtons.find(
        button =>
          scroller!.compareDocumentPosition(button) &
          Node.DOCUMENT_POSITION_FOLLOWING
      );
      const canScrollLeft = scroller.scrollLeft > 1;
      const canScrollRight =
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
      if (startButton) {
        startButton.style.display = canScrollLeft ? "" : "none";
      }
      if (endButton) {
        endButton.style.display = canScrollRight ? "" : "none";
      }
    };

    updateScrollButtonVisibility();

    /*
     * Observed on `document.body` rather than on the strip, for the reason in the docstring: the
     * strip may not exist when this runs. `window`'s resize event is not a substitute for the
     * `ResizeObserver` — the rail beside the strip can change width without the window changing at
     * all (a longer user name, a scrollbar appearing) and that is exactly when four tabs stop
     * fitting.
     */
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateScrollButtonVisibility)
        : undefined;
    resizeObserver?.observe(document.body);

    const mutationObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(updateScrollButtonVisibility)
        : undefined;
    mutationObserver?.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      scroller?.removeEventListener("scroll", updateScrollButtonVisibility);
    };
  }, [tabsId]);
};
